import { useEffect, useContext } from "react";
import { actions as searchActions } from "../../store/actions.js";
import { SearchBoxContext } from "../SearchBoxContext/index.js";
import { useDispatch, useSelector } from "react-redux";
import { getSearchbox } from "../../store/selectors.js";
const isDateRangeFetched = (dateRange, fetchedRanges, departureDate, fareCalendarData) => {
    const [requestStart, requestEnd] = dateRange;
    const relevantRanges = fetchedRanges.filter((range) => {
        if (departureDate) {
            return range.departureDate === departureDate;
        }
        return !range.departureDate;
    });
    for (let i = relevantRanges.length - 1; i >= 0; i--) {
        const range = relevantRanges[i];
        const rangeCoversRequest = range.start <= requestStart && range.end >= requestEnd;
        if (rangeCoversRequest && fareCalendarData) {
            const expectedSourceRange = `${range.start}_${range.end}${range.departureDate ? `_${range.departureDate}` : ""}`;
            if (fareCalendarData[expectedSourceRange]) {
                return true;
            }
        }
    }
    return false;
};
export const useFetchFareCalendar = ({ departureDateRange, returnDateRange, enabled = true, segmentIndex = 0, forceRefreshKey = 0 } = {}) => {
    const searchbox = useSelector(getSearchbox);
    const fareCalendar = searchbox.fareCalendar;
    const dispatch = useDispatch();
    const segment = searchbox.searchSegments[segmentIndex];
    const { autoCompleteBaseURL } = useContext(SearchBoxContext);
    const originLocation = segment?.from?.find((loc) => loc.type === "CITY" || loc.type === "AIRPORT");
    const destinationLocation = segment?.to?.find((loc) => loc.type === "CITY" || loc.type === "AIRPORT");
    const originCode = originLocation?.code;
    const destinationCode = destinationLocation?.code;
    const departureDateRangeStart = departureDateRange?.[0];
    const departureDateRangeEnd = departureDateRange?.[1];
    const returnDateRangeStart = returnDateRange?.[0];
    const returnDateRangeEnd = returnDateRange?.[1];
    const tripType = searchbox.type;
    const cabinClass = searchbox.cabinClass;
    const isDirect = searchbox.isDirect;
    const baseUrl = autoCompleteBaseURL?.url;
    useEffect(() => {
        if (!enabled || !departureDateRangeStart || !departureDateRangeEnd || !originLocation || !destinationLocation) {
            return;
        }
        const rangeToCheck = returnDateRange
            ? [returnDateRangeStart, returnDateRangeEnd]
            : [departureDateRangeStart, departureDateRangeEnd];
        const departureDateForCache = returnDateRange ? departureDateRangeStart : undefined;
        const alreadyFetched = isDateRangeFetched(rangeToCheck, fareCalendar.fetchedRanges, departureDateForCache, fareCalendar.data);
        if (alreadyFetched) {
            return;
        }
        dispatch(searchActions.fareCalendarStart());
        const payload = {
            origin: {
                cityIata: originLocation.type === "CITY" ? originLocation.code : undefined,
                airportIata: originLocation.type === "AIRPORT" ? originLocation.code : undefined
            },
            destination: {
                cityIata: destinationLocation.type === "CITY" ? destinationLocation.code : undefined,
                airportIata: destinationLocation.type === "AIRPORT" ? destinationLocation.code : undefined
            },
            departureDateRange: [departureDateRangeStart, departureDateRangeEnd],
            ...(returnDateRange && {
                returnDateRange: [returnDateRangeStart, returnDateRangeEnd]
            }),
            tripType: tripType === "ROUNDTRIP" ? "ROUND_TRIP" : "ONE_WAY",
            filters: {
                cabinClass: cabinClass,
                maxNumberOfStops: isDirect ? 0 : undefined
            }
        };
        const baseFareCalendarUrl = baseUrl ? `${baseUrl}/api/flyAnywhere/fareCalendar` : "/api/flyAnywhere/fareCalendar";
        const pageParams = new URLSearchParams(window.location.search);
        const expParams = new URLSearchParams();
        pageParams.forEach((value, key) => {
            if (key.startsWith("exp_")) {
                expParams.set(key, value);
            }
        });
        const expQuery = expParams.toString();
        const apiUrl = expQuery ? `${baseFareCalendarUrl}?${expQuery}` : baseFareCalendarUrl;
        fetch(apiUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(payload),
            credentials: "include"
        })
            .then((response) => response.json())
            .then((data) => {
            dispatch(searchActions.fareCalendarSuccess(data.fares || [], rangeToCheck, departureDateForCache, data.maxOfferWindowDays));
        })
            .catch((error) => {
            dispatch(searchActions.fareCalendarError(error.message || "Failed to fetch fare calendar data"));
        });
    }, [
        departureDateRangeStart,
        departureDateRangeEnd,
        returnDateRangeStart,
        returnDateRangeEnd,
        enabled,
        originCode,
        destinationCode,
        tripType,
        cabinClass,
        isDirect,
        baseUrl,
        forceRefreshKey
    ]);
    return {
        data: fareCalendar.data,
        loading: fareCalendar.loading,
        error: fareCalendar.error,
        departureDateRange: departureDateRange,
        returnDateRange: returnDateRange
    };
};
